import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Download, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { api, download, errMsg } from "@/lib/api";
import { SIM } from "@/components/dds";

const KEYS = [["submitted", "Submitted", "text-indigo-700"], ["available", "Available", "text-emerald-700"], ["rejected", "Rejected", "text-rose-700"], ["failed", "Failed", "text-rose-700"], ["skipped", "Skipped", "text-slate-500"]];

function RunProgress({ run, onRetry, testid = "bulk-progress" }) {
  const c = run.counts;
  const running = run.status === "running";
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={testid}>
      <div className="mb-1 flex items-center justify-between text-sm"><span className="font-semibold">{running ? "Submitting…" : "Run finished"} <span className="font-normal text-slate-500">{run.period} · by {run.started_by}</span></span>
        <span data-testid="bulk-progress-done">{c.done} / {c.total}</span></div>
      <Progress value={c.total ? (100 * c.done) / c.total : 0} className="h-2" />
      <div className="mt-2 flex flex-wrap gap-3 text-xs">{KEYS.map(([k, l, cl]) => <span key={k} className={cl} data-testid={`bulk-count-${k}`}><b>{c[k] || 0}</b> {l}</span>)}</div>
      {run.sim_label && <p className="mt-1 text-[11px] font-bold text-amber-700">{run.sim_label}</p>}
      {!running && <div className="mt-2 flex gap-2">
        <Button size="sm" variant="outline" data-testid="bulk-summary-csv-btn" onClick={() => download(`/dds/bulk/${run.id}/csv`, `bulk_submit_${run.period}.csv`)}><Download className="mr-1 h-3.5 w-3.5" />Summary CSV</Button>
        {(c.failed + c.rejected > 0) && <Button size="sm" data-testid="bulk-retry-btn" onClick={() => onRetry(run)}><RotateCcw className="mr-1 h-3.5 w-3.5" />Retry failed ({c.failed + c.rejected})</Button>}
      </div>}
      <details className="mt-2 text-xs"><summary className="cursor-pointer text-slate-500">Details</summary>
        {run.items.map((i) => <p key={i.dds_id} className="border-b border-slate-100 py-0.5"><span className="font-mono">{i.internal_ref}</span> · {i.result}{i.reason ? ` – ${i.reason}` : ""}{i.reference_number ? ` · ${i.reference_number} / ${i.verification_number}` : ""}</p>)}
      </details>
    </div>
  );
}

export function BulkSubmit({ filters, onDone }) {
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState(filters.period || "");
  const [pv, setPv] = useState(null);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const body = { period, plant: filters.plant || null, entity: filters.entity || null, activity: filters.activity && filters.activity !== "all" ? filters.activity : null };
  const check = async () => { setPv(null); if (!period) return; try { setPv((await api.post("/dds/bulk/preview", body)).data); } catch (e) { toast.error(errMsg(e)); } };
  useEffect(() => { if (open) check(); }, [open, period]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!run || run.status !== "running") return undefined;
    const t = setInterval(() => api.get(`/dds/bulk/${run.id}`).then((r) => { setRun(r.data); if (r.data.status !== "running") onDone?.(); }), 2000);
    return () => clearInterval(t);
  }, [run?.id, run?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = async () => { setBusy(true); try { setRun((await api.post("/dds/bulk", body)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const retry = async (r) => { try { setRun((await api.post(`/dds/bulk/${r.id}/retry`)).data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <>
      <Button data-testid="bulk-submit-open-btn" onClick={() => { setRun(null); setOpen(true); }}><Send className="mr-1.5 h-4 w-4" />Submit all ready</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="bulk-submit-dialog" className="max-w-xl">
          <DialogHeader><DialogTitle>Submit all ready DDS</DialogTitle></DialogHeader>
          {!run && <>
            <label className="text-sm">Period<input data-testid="bulk-period-input" type="month" className="ml-2 h-9 rounded-md border px-2" value={period} onChange={(e) => setPeriod(e.target.value)} /></label>
            <p className="text-xs text-slate-500">Filters: plant {body.plant || "all"} · entity {body.entity ? "selected" : "all"} · activity {body.activity || "all"}</p>
            {pv && <div className="rounded-lg border border-slate-200 p-3 text-sm" data-testid="bulk-preview">
              <p><b data-testid="bulk-ready-count">{pv.ready}</b> Ready draft(s) will be submitted through the TRACES adapter.</p>
              <p className="text-rose-700"><b data-testid="bulk-excluded-count">{pv.excluded}</b> Blocked / invalid draft(s) excluded – never submitted.</p>
              {pv.excluded_items.slice(0, 6).map((x) => <p key={x.id} className="text-xs text-slate-500"><span className="font-mono">{x.internal_ref}</span> · {x.reason}</p>)}
              {pv.mode === "simulation" && <p className="mt-2 text-xs font-bold text-amber-700">{SIM}</p>}
              {pv.running && <p className="mt-2 text-xs font-semibold text-rose-700">A bulk submission is already running</p>}
            </div>}
          </>}
          {run && <RunProgress run={run} onRetry={retry} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            {!run && <Button data-testid="bulk-confirm-btn" disabled={busy || !pv || !pv.ready} onClick={go}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Confirm – submit {pv?.ready ?? 0}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
